<template>
	<div>
		<div class="row gutter-md">
			<div class="col-xs-12 col-sm-12">
				<p class="caption">{{ $t('import_vouchers') }}</p>

				<uploader
					content_type="xlsx"
					@fileUploaded="fileUploaded"
					:multiple="false"
					:cropper="false"
				/>
			</div>

			<div class="col-xs-12 col-sm-12">
				<q-table
					:data="vouchers"
					:columns="columns"
					:pagination.sync="server_pagination"
					@request="request"
					:loading="loading"
					:filter="filter"
					row-key="id"
					:no-data-label="$t('no_data_avaliable')"
					:no-results-label="$t('no_results_found')"
					:loading-label="$t('loading_data')"
					:rows-per-page-label="$t('items_per_page')"
					:rows-per-page-options="[5, 10, 50, 100]"
					binary-state-sort
				>
					<template slot="top" slot-scope="props" :props="props">
						<div class="q-table-title">{{ $t('redeen_codes') }}</div>
						<div class="col" />
						<q-btn
							color="secondary"
							icon="far fa-plus"
							:label="$t('new')"
							@click="show_redeem_codes = true"
						/>
						<div class="col-12 q-my-sm">
							<q-search
								v-model="filter"
								icon="far fa-search"
								:placeholder="$t('search')"
								:debounce="500"
							/>
						</div>
					</template>

					<q-tr slot="header" slot-scope="props" :props="props">
						<q-th v-for="col in props.cols" :key="col.name" :props="props">
							{{ $t(col.label) }}
						</q-th>
					</q-tr>

					<q-tr slot="body" slot-scope="props" :props="props">
						<q-td auto-width key="id" :props="props">
							{{ props.row.id }}
						</q-td>

						<q-td key="redeen_code" :props="props">
							{{ props.row.redeen_code }}
						</q-td>

						<q-td key="order_number" :props="props">
							{{ props.row.order_number }}
						</q-td>

						<q-td key="user_full_name" :props="props">
							{{ props.row.user_full_name }}
						</q-td>

						<q-td key="user_email" :props="props">
							{{ props.row.user_email }}
						</q-td>

						<q-td key="status" :props="props" auto-width>
							<q-chip
								small
								:icon="getIconStatus(props.row.status)"
								:color="getColorCodeStatus(props.row.status)"
								class="full-width"
							>
								{{ $t(getStatus(props.row.status)) }}
							</q-chip>
						</q-td>

						<q-td key="actions" :props="props">
							<q-btn
								flat
								round
								color="negative"
								:disable="disableActionVouchers(props.row.status)"
								icon="far fa-trash-alt"
								@click="confirmRemove(props.row.actions)"
							/>
						</q-td>
					</q-tr>
				</q-table>
			</div>
		</div>

		<div class="row gutter-sm justify-end q-mt-lg">
			<div class="col-xs-12 col-sm-3" :class="getOrder('cancel')">
				<q-btn
					color="tertiary"
					class="full-width"
					:label="$t('back')"
					icon="far fa-chevron-left"
					@click="$router.push({ name: 'rewards' })"
				/>
			</div>
		</div>

		<q-dialog v-model="confirm_remove" @ok="deleteItem()">
			<span slot="title">{{ $t('remove_item') }}</span>

			<span slot="message">{{ $t('confirm_remove_item') }}</span>

			<template slot="buttons" slot-scope="props" :props="props">
				<q-btn color="primary" :label="$t('cancel')" @click="confirm_remove = false" />
				<q-btn color="negative" :label="$t('remove')" @click="deleteItem()" />
			</template>
		</q-dialog>

		<q-modal
			v-model="show_redeem_codes"
			:content-css="{ minWidth: getMinWidth, maxWidth: getMaxWidth, minHeight: '80vh' }"
			@hide="hideCodeModal()"
			no-backdrop-dismiss
			no-esc-dismiss
		>
			<q-modal-layout>
				<q-toolbar slot="header">
					<q-btn flat round dense @click="hideCodeModal()" icon="fas fa-times" />
					<q-toolbar-title>
						{{ $t('new_redeen_code') }}
					</q-toolbar-title>
				</q-toolbar>

				<div class="layout-padding">
					<div class="row gutter-sm justify-end">
						<div class="col-xs-12 col-sm-2">
							<q-btn
								color="positive"
								class="full-width"
								:label="$t('add')"
								icon="far fa-plus"
								@click="addItem()"
							/>
						</div>
					</div>

					<div class="row gutter-sm" v-for="(code, index) in redeem_codes" :key="index">
						<div
							class=""
							:class="index === 0 ? 'col-xs-12 col-sm-12' : 'col-xs-11 col-sm-11'"
						>
							<q-input v-model="code.code" :float-label="$t('redeen_code')" />
						</div>

						<div class="col-xs-1 col-sm-1" v-if="index > 0">
							<q-btn
								flat
								round
								color="negative"
								class="full-width"
								icon="far fa-trash"
								@click="removeItem(index)"
							/>
						</div>
					</div>

					<div class="row gutter-sm justify-end q-mt-lg">
						<div class="col-xs-12 col-sm-3" :class="getOrder('cancel')">
							<q-btn
								color="tertiary"
								class="full-width"
								:label="$t('cancel')"
								icon="far fa-block"
								@click="hideCodeModal()"
							/>
						</div>

						<div class="col-xs-12 col-sm-3" :class="getOrder('save')">
							<q-btn
								color="secondary"
								class="full-width"
								:label="$t('save')"
								icon="far fa-save"
								@click="saveCode()"
							/>
						</div>
					</div>
				</div>
			</q-modal-layout>
		</q-modal>
	</div>
</template>

<script>
import { mapGetters } from 'vuex'
import { required, requiredIf, maxValue, minValue } from 'vuelidate/lib/validators'
import Uploader from 'components/Uploader'

export default {
	props: ['id_reward'],

	i18nOptions: { namespaces: ['custom', 'common', 'training', 'admin', 'rewards'] },

	data() {
		return {
			columns: [
				{
					align: 'left',
					field: 'id',
					label: '#',
					name: 'id',
					required: true,
					sortable: true,
				},
				{
					align: 'left',
					field: 'redeen_code',
					label: 'redeen_code',
					name: 'redeen_code',
					required: true,
					sortable: true,
				},
				{
					align: 'left',
					field: 'order_number',
					label: 'order_number',
					name: 'order_number',
					required: true,
					sortable: true,
				},
				{
					align: 'left',
					field: 'user_full_name',
					label: 'name',
					name: 'user_full_name',
					required: true,
					sortable: true,
				},
				{
					align: 'left',
					field: 'user_email',
					label: 'email',
					name: 'user_email',
					required: true,
					sortable: true,
				},
				{
					align: 'left',
					field: 'status',
					label: 'status',
					name: 'status',
					required: true,
					sortable: true,
				},
				{
					align: 'right',
					field: 'edit',
					label: '',
					name: 'actions',
					required: true,
					sortable: false,
				},
			],
			confirm_remove: false,
			loading: false,
			msg: '',
			filter: '',
			redeem_codes: [
				{
					code: '',
				},
			],
			server_pagination: {
				descending: false,
				page: 1,
				rowsNumber: 10,
				rowsPerPage: 5,
				sortBy: null,
			},
			show_redeem_codes: false,
			vouchers: [],
		}
	},

	components: {
		uploader: Uploader,
	},

	computed: {
		...mapGetters({
			access_token: 'common/access_token',
		}),

		getHeader() {
			let headers = {}
			return (headers = {
				headers: {
					'content-type': 'application/json',
					authorization:
						'Basic ' +
						btoa(process.env.APP_CLIENT_ID + ':' + process.env.APP_CLIENT_SECRET),
					authorization: 'Bearer ' + this.access_token,
				},
			})
		},

		getMaxWidth() {
			if (this.$q.platform.is.mobile) return '100vw'
			else return '80vw'
		},

		getMinWidth() {
			if (!this.$q.platform.is.mobile) return '90vw'
		},
	},

	methods: {
		request({ pagination }) {
			this.server_pagination = pagination

			let order = pagination.descending ? '-' : ''
			let order_by = pagination.sortBy ? order + pagination.sortBy : ''

			let data = {
				page_size: this.server_pagination.rowsPerPage,
				page: this.server_pagination.page,
				ordering: order_by,
			}

			this.saveFilter()
			this.loading = true

			this.$axios
				.get(
					`/admin_rewards/rewards/${this.id_reward}/redeen_codes/?${this.$qs.stringify(
						data
					)}`,
					this.getHeader
				)
				.then((response) => {
					this.server_pagination.rowsNumber = response.data.count

					this.vouchers = []

					response.data.forEach((e) => {
						this.vouchers.push({
							id: e.id,
							status: e.status,
							order_number: e.order_number,
							user_email: e.user_email,
							user_full_name: e.user_full_name,
							redeen_code: e.redeen_code,
							actions: e.id,
						})
					})

					this.loading = false
				})
				.catch((e) => {
					this.loading = false
					console.log(e)
				})
		},

		deleteItem() {
			this.$q.loading.show()

			this.$axios
				.delete(
					`/admin_rewards/rewards/${this.id_reward}/redeen_codes/${this.action_id}/`,
					this.getHeader
				)
				.then((response) => {
					this.$q.loading.hide()

					this.request({
						pagination: this.server_pagination,
					})

					this.confirm_remove = false

					this.$q.notify({
						message: this.$t('item_removed_success'),
						type: 'positive',
						icon: 'far fa-trash-alt',
					})
				})
				.catch((e) => {
					this.$q.loading.hide()
					console.log(e)
				})
		},

		saveCode() {
			let data = { redeem_codes: [] }
			this.redeem_codes.forEach((element) => {
				if (element.code !== '') data.redeem_codes.push(element.code)
			})

			if (data.redeem_codes.length > 0) {
				this.$q.loading.show()

				this.$axios
					.post(
						`/admin_rewards/rewards/${this.id_reward}/redeen_codes/`,
						data,
						this.getHeader
					)
					.then((response) => {
						this.hideCodeModal()

						this.$q.notify({
							message: this.$t('redeem_codes_added_successfully'),
							type: 'positive',
							icon: 'fal fa-save',
						})
					})
					.catch((e) => {
						console.log(e)
					})
					.finally(() => {
						this.$q.loading.hide()
					})
			} else {
				this.hideCodeModal()
			}
		},

		fileUploaded(file) {
			this.$q.loading.show()

			let data = {
				file_url: file,
			}

			this.$axios
				.post(
					`/admin_rewards/rewards/${this.id_reward}/redeen_codes/load_import_file/`,
					data,
					this.getHeader
				)
				.then((response) => {
					this.redeem_codes = response.data.redeem_codes.map((e) => ({ code: e }))
					this.show_redeem_codes = true
				})
				.catch((e) => {
					console.log(e)
				})
				.finally(() => {
					this.$q.loading.hide()
				})
		},

		hideCodeModal() {
			this.redeem_codes = [{ code: '' }]
			this.show_redeem_codes = false

			this.request({
				pagination: this.server_pagination,
			})
		},

		disableActionVouchers(status) {
			if (status === 4 || status === 3) {
				return true
			} else return false
		},

		addItem() {
			this.redeem_codes.push({ code: '' })
		},

		removeItem(index) {
			this.redeem_codes.splice(index, 1)
		},

		confirmRemove(id) {
			this.action_id = id
			this.confirm_remove = true
		},

		getOrder(name) {
			if (name == 'cancel') {
				if (this.$q.platform.is.mobile) return 'order-last'
				else return 'order-first'
			} else if (name == 'save') {
				if (this.$q.platform.is.mobile) return 'order-first'
				else return 'order-last'
			}
		},

		saveFilter() {
			let dataTableCode = {
				rowsPerPage: this.server_pagination.rowsPerPage,
				filter: this.filter,
			}
			this.$q.localStorage.set('dataTableCode', dataTableCode)
		},

		loadFilter() {
			if (this.$q.localStorage.has('dataTableCode')) {
				let redeen_code = this.$q.localStorage.get.item('dataTableCode')
				this.server_pagination.rowsPerPage = redeen_code.rowsPerPage
				this.filter = redeen_code.filter
			}
		},

		getIconStatus(status) {
			if (status == 1) return 'fas fa-check'
			else if (status == 2) return 'fas fa-ban'
			else if (status == 3) return 'fas fa-trash-alt'
			else if (status == 4) return 'fas fa-clipboard-check'
			else return ''
		},

		getColorCodeStatus(status) {
			if (status == 1) return 'positive'
			else if (status == 2) return 'warning'
			else if (status == 3) return 'negative'
			else if (status == 4) return 'brown-5'
			else return ''
		},

		getStatus(status) {
			if (status == 1) return 'available'
			else if (status == 2) return 'unavailable'
			else if (status == 3) return 'deleted'
			else if (status == 4) return 'used'
			else return ''
		},

		insertImg() {
			const input = document.createElement('input')
			input.type = 'file'
			input.accept = '.png, .jpg, .gif'
			let file

			input.onchange = (_) => {
				const files = Array.from(input.files)
				file = files[0]

				const reader = new FileReader()
				let dataUrl = ''
				reader.onloadend = function() {
					dataUrl = reader.result
					document.execCommand(
						'insertHTML',
						true,
						'<div><img src="' + dataUrl + '" style="max-width: 100%" /></div>'
					)
				}
				reader.readAsDataURL(file)
			}
			input.click()
		},
	},

	created() {
		this.loadFilter()

		this.request({
			pagination: this.server_pagination,
		})
	},
}
</script>

<style></style>
